import './App.css';
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { charolasDeCombos, totalesDePacking } from './utils/packingFromCombos';
import { CartProvider } from './context/CartContext';
import { CustomerIdentityProvider } from './context/CustomerIdentityContext';
import { Toaster } from 'sonner';
import { assetUrl, handleImgError } from './utils/imageHelpers';
import Navbar from './components/Navbar';
import HeroSection from './pages/HeroSection';
import MenuSection from './pages/MenuSection';
import ModalBilling from './components/ModalBilling';
import ModalLoader from './components/ModalLoader';
import ComboWizard from './components/ComboWizard';
import ComboChooser from './components/ComboChooser';
import AnnouncementModal from './components/AnnouncementModal';
import { comboDeNovedad, wasAnnouncementSeen, markAnnouncementSeen } from './utils/announcements';
import { parseCombos, buildSteps, combosParaPlatillo, opcionesDelCombo } from './utils/combos';
import { loadMenuDay, resetMenuDay, getOrderingWindow } from './utils/menu';
import { loadBusinessHours } from './utils/businessHours';
import { API_URL } from './utils/api';

function App() {
  const [showBillingModal, setShowBillingModal] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const [loaderProgress, setLoaderProgress] = useState(0);
  const [orderCode, setOrderCode] = useState('');
  const [menuReloadFlag, setMenuReloadFlag] = useState(false);
  const [comboWizard, setComboWizard] = useState({ open: false, combo: null, editingGroupId: null, initialSelections: null, noDiscount: false });
  const [combos, setCombos] = useState([]);
  const [dayItems, setDayItems] = useState([]);
  // Ventana de pedidos del menú vigente: en modo vitrina (open=false) el sitio
  // se ve completo pero no deja ordenar.
  const [ordering, setOrdering] = useState({ open: true, opensAt: null, menuDate: null });
  const [comboChooser, setComboChooser] = useState({ open: false, product: null });
  const [announcement, setAnnouncement] = useState(null);
  // El gate de identificación espera a saber si hay novedad: si no, se apilan.
  const [announcementChecked, setAnnouncementChecked] = useState(false);

  // Novedad vigente (la programa el negocio en el panel). Si el endpoint falla
  // el sitio sigue igual, sin modal.
  useEffect(() => {
    let active = true;
    fetch(`${API_URL}/announcements/current`)
      .then((res) => res.json())
      .then((json) => {
        const a = json.status === 'success' ? json.data : null;
        if (!active || !a || wasAnnouncementSeen(a)) return;
        markAnnouncementSeen(a);
        setAnnouncement(a);
      })
      .catch((err) => console.error('Error al obtener la novedad:', err))
      .finally(() => { if (active) setAnnouncementChecked(true); });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    let active = true;
    fetch(`${API_URL}/promotions/promotions`)
      .then((res) => res.json())
      .then((json) => {
        if (active && json.status === 'success') setCombos(parseCombos(json.data));
      })
      .catch((err) => console.error('Error al obtener combos:', err));
    return () => { active = false; };
  }, []);

  // Items del día: sirven para validar qué categorías tienen opción seleccionable
  // hoy y así ocultar combos incompletables (p.ej. con postre cuando no hay postres).
  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const bh = await loadBusinessHours();
        const menuDay = await loadMenuDay(bh);
        if (!active) return;
        setDayItems(menuDay.items);
        setOrdering({ ...getOrderingWindow(menuDay.date, bh), menuDate: menuDay.date });
      } catch (err) {
        console.error('Error al obtener items del día (combos):', err);
      }
    };
    load();
    return () => { active = false; };
  }, [menuReloadFlag]);

  // Categorías con al menos una opción seleccionable hoy (mismo criterio que el
  // wizard: promocionable + con stock).
  const availableCats = useMemo(() => {
    const set = new Set();
    for (const it of dayItems) {
      if (Number(it.apply_promotions) === 1 && (it.product_counter || 0) > 0) {
        set.add(Number(it.category_id));
      }
    }
    return set;
  }, [dayItems]);

  // Si esa categoría del combo tiene opción hoy. Con lista (allowed_items) hace
  // falta algo de la lista: el combo "con agua" no se ofrece si no hay agua.
  const catDisponible = useCallback((combo, cat) => {
    const lista = (combo.allowedItems || {})[cat];
    if (!Array.isArray(lista) || lista.length === 0) return availableCats.has(Number(cat));
    return opcionesDelCombo(
      combo,
      Number(cat),
      dayItems.filter((it) => Number(it.category_id) === Number(cat)
        && Number(it.apply_promotions) === 1 && (it.product_counter || 0) > 0)
    ).length > 0;
  }, [availableCats, dayItems]);

  // Un combo solo se ofrece si TODAS sus categorías tienen opción hoy. La
  // categoría del producto sembrado cuenta como satisfecha por el propio seed.
  // Con combos por platillo (main_items) el chooser solo muestra los que ese
  // platillo puede armar.
  const availableCombos = useMemo(() => {
    const product = comboChooser.product;
    const seedCat = product ? Number(product.categoryId) : null;
    const completables = combos.filter((combo) =>
      (combo.categories || []).every(
        (cat) => catDisponible(combo, cat)
          // El producto sembrado cubre su categoría si el combo lo acepta.
          || (Number(cat) === seedCat
            && (seedCat === 1 || opcionesDelCombo(combo, seedCat, [product]).length > 0))
      )
    );
    return product && seedCat === 1 && Number(product.applyPromotions) === 1
      ? combosParaPlatillo(completables, product.id, combos)
      : completables;
  }, [combos, catDisponible, comboChooser.product]);

  // Ancla de precio de las cards: el combo completable más barato de hoy PARA
  // ESE platillo (la Cordon arranca en su Premium, no en el Juanita). La card
  // no puede anunciar el precio de la porción, que es menor al del combo.
  const comboFromPrice = useCallback((productId) => {
    const usables = combosParaPlatillo(
      combos.filter((combo) =>
        (combo.categories || []).every((cat) => catDisponible(combo, cat))
      ),
      productId,
      combos
    );
    return usables.length ? Math.min(...usables.map((c) => c.price)) : null;
  }, [combos, catDisponible]);

  const handleEditCombo = (group) => {
    const meta = group.meta || {};
    const combo = {
      id: meta.comboId,
      name: meta.name,
      price: meta.price,
      categories: meta.categories || [],
      composition: meta.composition || '',
      mainItems: combos.find((c) => c.id === meta.comboId)?.mainItems || [],
      allowedItems: combos.find((c) => c.id === meta.comboId)?.allowedItems || {},
    };
    setComboWizard({ open: true, combo, editingGroupId: group.groupId, initialSelections: group.items, noDiscount: !!meta.noDiscount });
  };

  const closeComboWizard = () => {
    setComboWizard({ open: false, combo: null, editingGroupId: null, initialSelections: null, noDiscount: false });
  };

  // Combo button on a product card: pick a combo, then open the existing
  // wizard with this product preset as its platillo.
  const handleArmarCombo = (product) => {
    setComboChooser({ open: true, product });
  };

  const handlePickCombo = (combo) => {
    const product = comboChooser.product;
    // Platillo no elegible para promociones → arma "plato" a precio lleno.
    const noDiscount = product ? Number(product.applyPromotions) !== 1 : false;
    let initialSelections = null;
    if (product) {
      const steps = buildSteps(combo.categories);
      const platilloStep = steps.findIndex((s) => s.cat === (product.categoryId || 1));
      if (platilloStep >= 0) {
        initialSelections = steps.map((_, i) => (i === platilloStep ? { id: product.id } : null));
      }
    }
    setComboChooser({ open: false, product: null });
    setComboWizard({ open: true, combo, editingGroupId: null, initialSelections, noDiscount });
  };

  // "Ordenar" de la novedad: su combo acotado a los productos marcados. Si hoy
  // no se puede armar (vitrina, agotado, sin combo) el botón lleva al menú.
  const announcementCombo = useMemo(() => {
    if (!announcement || ordering.open === false) return null;
    const completable = (c) => (c.categories || []).every((cat) => catDisponible(c, cat));
    const novedad = comboDeNovedad(announcement, combos);
    if (!novedad) return null;
    if (completable(novedad.combo)) return novedad;
    const base = combos.find((c) => Number(c.id) === Number(announcement.cta_combo_id));
    return completable(base) ? { combo: base, initialSelections: null } : null;
  }, [announcement, combos, catDisponible, ordering.open]);

  const closeAnnouncement = useCallback(() => setAnnouncement(null), []);

  const handleAnnouncementCta = () => {
    setAnnouncement(null);
    if (announcementCombo) {
      setComboWizard({ open: true, editingGroupId: null, noDiscount: false, ...announcementCombo });
      return;
    }
    document.getElementById('menu')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleCartClick = () => {
    // En modo vitrina no hay checkout: el menú se ve, pero los pedidos abren después.
    if (!ordering.open) return;
    setShowBillingModal(true);
  };

  const handleCloseBilling = () => {
    setShowBillingModal(false);
    // Force a complete reset - this ensures everything returns to initial state
    window.scrollTo({ top: 0, behavior: 'smooth' });
    // Trigger reload of menu items (el caché del día se invalida para que
    // las existencias se relean tras el pedido)
    resetMenuDay();
    setMenuReloadFlag(flag => !flag);
  };
  
  const handleStartProcessing = async (orderData, onComplete) => {
    setShowLoader(true);
    setLoaderProgress(0);

    // Obtener el último id de venta y sumar 1 para el nuevo código de orden
    let generatedOrderCode = '';
    try {
      const response = await fetch(`${API_URL}/orders/get-last-sale`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      if (response.ok) {
        const result = await response.json();
        if (result.status === 'success' && typeof result.data === 'number') {
          generatedOrderCode = (result.data + 1).toString();
        } else {
          // Fallback: si la respuesta no es válida, usar 1
          generatedOrderCode = '1';
        }
      } else {
        // Fallback: si la petición falla, usar 1
        generatedOrderCode = '1';
      }
    } catch (err) {
      // Fallback: si hay error, usar 1
      generatedOrderCode = '1';
    }
    setOrderCode(generatedOrderCode);
    // Código anterior (letras y números aleatorios)
    // const generatedOrderCode = Math.random().toString(36).substring(2, 10).toUpperCase();
    // setOrderCode(generatedOrderCode);

    try {
      const progressInterval = setInterval(() => {
        setLoaderProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 5;
        });
      }, 250);

      // If it's a new customer, add them to the database first
      if (orderData.isNewCustomer) {
        await addCustomer(orderData.customerInfo.name, orderData.customerInfo.phone);
      }

      // Prepare order data for saving with promotions and discounted price
      // Compose delivery_datetime from selected date and hour/minute
      const deliveryDate = orderData.customerInfo.date;
      const deliveryHour = orderData.customerInfo.hour?.toString().padStart(2, '0') || '00';
      const deliveryMinute = orderData.customerInfo.minute?.toString().padStart(2, '0') || '00';
      const delivery_datetime = `${deliveryDate} ${deliveryHour}:${deliveryMinute}`;

      // Charolas para cocina. Cada combo del wizard es una charola con la misma
      // forma que arma el punto de venta (ver utils/packingFromCombos): las
      // bebidas y postres del combo van como sueltos ligados a su charola. Van
      // primero, así que las ligas del empaquetado de sueltos se recorren.
      const { charolas: comboCharolas, sueltos: comboExtras } = charolasDeCombos(orderData.comboGroups);
      const loosePacking = orderData.packingConfig;
      const looseCharolas = (loosePacking && loosePacking.charolas) || [];
      // Si se saltó el paso de empaquetado (sin platillo suelto), los sueltos
      // van directo al packing_config: cuando este existe, cocina ya no
      // muestra la lista plana y los perdería.
      const looseSueltos = loosePacking
        ? (loosePacking.sueltos || []).map((s) => ({
            ...s,
            linkedToCharola: s.linkedToCharola != null ? s.linkedToCharola + comboCharolas.length : null,
          }))
        : orderData.items
            .filter(it => !it.comboGroupId)
            .flatMap(it => Array.from({ length: it.quantity }, () => ({
              id: it.id,
              name: it.title,
              categoryId: it.categoryId,
              price: it.price,
              applyPromotions: it.applyPromotions,
              presentation: it.presentation,
              presentationLabel: it.presentationLabel,
              qty: it.qty,
              unit: it.unit,
              linkedToCharola: null,
            })));
      // Un carrito sin platillo no genera loosePacking (computePackingConfig
      // devuelve null), pero el tamaño de un suelto SOLO viaja aqui: items
      // guarda {id: cantidad} y Cocina lo veria como porcion. Mismo guard que
      // el POS (management/src/components/ModalBilling.js).
      const hasNonDefaultSize = looseSueltos
        .some((s) => s.presentation && s.presentation !== 'porcion');
      const charolasParaCocina = [...comboCharolas, ...looseCharolas];
      const sueltosParaCocina = [...comboExtras, ...looseSueltos];
      const packingForKitchen = (charolasParaCocina.length > 0 || loosePacking || hasNonDefaultSize)
        ? {
            charolas: charolasParaCocina,
            sueltos: sueltosParaCocina,
            ...totalesDePacking(charolasParaCocina, sueltosParaCocina),
          }
        : null;

      const saveData = {
        phone: orderData.customerInfo.phone,
        items: {},
        total_amount: parseFloat(orderData.subtotalPreDiscount ?? orderData.totalPrice), // subtotal pre-descuento; el backend aplica el descuento de cliente
        maps_url: '',
        promotions: orderData.promotions || {}, // Use promotions from orderData (dict_combos_apply)
        code_order: generatedOrderCode,
        delivery_datetime,
        packing_config: packingForKitchen,
        payment_method: orderData.paymentMethod || 'efectivo',
        employee_discount: orderData.employeeDiscount || 0,
        benefit_combos: orderData.benefitCombos || 0,
        coupon_id: orderData.couponId || null,
        // Lo que el cliente pidió avisar a cocina (sin cebolla, salsa extra).
        notes: (orderData.notes || '').trim() || undefined
      };

      // Convert cart items to the required format
      orderData.items.forEach(item => {
        saveData.items[item.id] = (saveData.items[item.id] || 0) + item.quantity;
      });

      // Set maps_url based on delivery type
      if (orderData.deliveryType === 'delivery' && orderData.location) {
        saveData.maps_url = orderData.location;
      } else if (orderData.deliveryType === 'someone_else' && orderData.customAddress.street.trim()) {
        const fullAddress = `${orderData.customAddress.street.trim()} ${orderData.customAddress.number.trim()}, ${orderData.customAddress.neighborhood.trim()}, Montemorelos, Nuevo León`;
        saveData.maps_url = fullAddress;
      }

      // Save the order to the database
      await saveOrder(saveData);
      
      // Complete progress
      clearInterval(progressInterval);
      setLoaderProgress(100);

      // Wait for completion animation
      setTimeout(() => {
        setShowLoader(false);
        setLoaderProgress(0);
        
        // Show success modal with order data and code
        setTimeout(() => {
          onComplete(orderData);
        }, 500);
      }, 2000);

    } catch (error) {
      console.error('Error processing order:', error);
      setShowLoader(false);
      setLoaderProgress(0);
      if (error.slotFull) {
        alert(`${error.message}\n\nTu pedido no se ha enviado. Regresa al paso de entrega y elige otro horario.`);
      } else if (error.couponInvalid) {
        alert(`${error.message}\n\nTu pedido no se ha enviado. Quita el cupón e intenta de nuevo.`);
      } else {
        alert('Error al procesar el pedido. Por favor intenta de nuevo.');
      }
    }
  };

  // Helper functions (moved from ModalBilling)
  const addCustomer = async (name, phone) => {
    try {
      const response = await fetch(`${API_URL}/customers/add-customer?name=${encodeURIComponent(name)}&phone=${phone}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error adding customer:', error);
      alert('Error al registrar el cliente. El pedido se enviará de todas formas.');
      return null;
    }
  };

  const saveOrder = async (orderData) => {
    try {
      // Create query parameters
      const params = new URLSearchParams();
      params.append('phone', orderData.phone);
      params.append('total_amount', orderData.total_amount);
      params.append('items', JSON.stringify(orderData.items));
      params.append('maps_url', orderData.maps_url || '');
      params.append('promotions', JSON.stringify(orderData.promotions));
      params.append('code_order', orderData.code_order);  // Add the order code parameter
      if (orderData.delivery_datetime) {
        params.append('delivery_datetime', orderData.delivery_datetime);
      }
      if (orderData.packing_config) {
        params.append('packing_config', JSON.stringify(orderData.packing_config));
      }
      if (orderData.payment_method) {
        params.append('payment_method', orderData.payment_method);
      }
      params.append('employee_discount', orderData.employee_discount || 0);
      params.append('benefit_combos', orderData.benefit_combos || 0);
      if (orderData.coupon_id) {
        params.append('coupon_id', orderData.coupon_id);
      }
      if (orderData.notes) {
        params.append('notes', orderData.notes);
      }

      const response = await fetch(`${API_URL}/orders/save-order?${params.toString()}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        // 409 SLOT_FULL: otro cliente llenó ese horario mientras este pedía
        let detail = null;
        try {
          const body = await response.json();
          detail = body?.detail || null;
        } catch (parseError) {
          detail = null;
        }
        if (response.status === 409 && detail?.code === 'SLOT_FULL') {
          const slotError = new Error(detail.message || 'Ese horario de entrega se acaba de llenar. Por favor elige otra hora.');
          slotError.slotFull = true;
          throw slotError;
        }
        if (response.status === 400 && detail?.code === 'COUPON_INVALID') {
          const couponError = new Error(detail.message || 'Ese cupón ya no es válido.');
          couponError.couponInvalid = true;
          throw couponError;
        }
        throw new Error('Network response was not ok');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error saving order:', error);
      if (!error.slotFull && !error.couponInvalid) {
        alert('Error al guardar el pedido.');
      }
      throw error;
    }
  };

  return (
    <CustomerIdentityProvider>
    <CartProvider>
      <div className="min-h-screen bg-gradient-to-b from-red-900 via-red-800 to-red-950 relative"
           style={{background: 'linear-gradient(180deg, #820000 0%, #860000 50%, #200000 100%)', overflowX: 'clip'}}>
        
        {/* Toast notifications */}
        <Toaster 
          position="top-right" 
          richColors 
          closeButton
          toastOptions={{
            style: {
              background: '#fff',
              color: '#000',
              border: '1px solid #e5e7eb',
            },
          }}
        />
        
        {/* Modal de loader */}
        <ModalLoader isOpen={showLoader} progress={loaderProgress} />
        
        {/* Modal de facturación */}
        <ModalBilling
          isOpen={showBillingModal}
          onClose={handleCloseBilling}
          onStartProcessing={handleStartProcessing}
          orderCode={orderCode}
          onEditCombo={handleEditCombo}
        />

        {/* Combo chooser (desde la card de producto) */}
        <ComboChooser
          isOpen={comboChooser.open}
          product={comboChooser.product}
          combos={availableCombos}
          noDiscount={comboChooser.product ? Number(comboChooser.product.applyPromotions) !== 1 : false}
          onPick={handlePickCombo}
          onClose={() => setComboChooser({ open: false, product: null })}
        />

        {/* Novedad al entrar (se configura en el panel) */}
        <AnnouncementModal
          announcement={announcement}
          ctaLabel={announcementCombo ? announcement?.cta_label || 'Ordenar' : 'Ver menú'}
          onCta={handleAnnouncementCta}
          onClose={closeAnnouncement}
        />

        {/* Combo builder wizard */}
        <ComboWizard
          isOpen={comboWizard.open}
          combo={comboWizard.combo}
          combos={combos}
          editingGroupId={comboWizard.editingGroupId}
          initialSelections={comboWizard.initialSelections}
          noDiscount={comboWizard.noDiscount}
          onClose={closeComboWizard}
        />
        
        {/* Navbar */}
        <Navbar
          onCartClick={handleCartClick}
          orderingOpen={ordering.open !== false}
          holdGate={!announcementChecked || !!announcement || comboWizard.open}
        />
        
        {/* Watermark logo pattern - hidden on mobile */}
        <div className="absolute inset-0 pointer-events-none z-0 h-full overflow-hidden hidden lg:block">
          {/* Primera fila */}
          <img src={assetUrl('/images/logo.png')} alt="" 
               className="absolute w-70 h-auto top-[5%] right-[0%] rotate-[15deg]"
               onError={handleImgError} />
          
          {/* Segunda fila */}
          {/*<img src={assetUrl('/images/logo.png')} alt="" 
               className="absolute w-72 h-auto top-[15%] left-[10%] -rotate-[12deg]"
               onError={handleImgError} />*/}
          
          {/* Tercera fila */}
          {/*<img src={assetUrl('/images/logo.png')} alt="" 
               className="absolute w-76 h-auto top-[25%] right-[12%] rotate-[-30deg]"
               onError={handleImgError} />*/}
          
          {/* Cuarta fila */}
          {/*<img src={assetUrl('/images/logo.png')} alt="" 
               className="absolute w-72 h-auto top-[35%] left-[18%] -rotate-[-45deg]"
               onError={handleImgError} />*/}
          
          {/* Quinta fila */}
          {/*<img src={assetUrl('/images/logo.png')} alt="" 
               className="absolute w-60 h-auto top-[45%] right-[50%] rotate-[0deg]"
               onError={handleImgError} />*/}
          
          {/* Sexta fila */}
          {/*<img src={assetUrl('/images/logo.png')} alt="" 
               className="absolute w-76 h-auto top-[55%] left-[15%] -rotate-[15deg]"
               onError={handleImgError} />*/}
          
          {/* Séptima fila */}
          {/*<img src={assetUrl('/images/logo.png')} alt="" 
               className="absolute w-80 h-auto top-[65%] right-[18%] rotate-[10deg]"
               onError={handleImgError} />*/}
          
          {/* Octava fila */}
          <img src={assetUrl('/images/logo.png')} alt="" 
               className="absolute w-70 h-auto top-[75%] left-[12%] -rotate-[22deg]"
               onError={handleImgError} />
          
          {/* Novena fila */}
          {/*<img src={assetUrl('/images/logo.png')} alt="" 
               className="absolute w-78 h-auto top-[85%] right-[14%] rotate-[16deg]"
               onError={handleImgError} />*/}
        </div>

        {/* Content */}
        <div className="relative z-10">
          <div id="hero">
            <HeroSection />
          </div>
          {/*<div id="destacados">
            <TopSection />
          </div>*/}
          <div id="menu">
            <MenuSection
              reloadFlag={menuReloadFlag}
              onArmarCombo={handleArmarCombo}
              ordering={ordering}
              comboFromPrice={comboFromPrice}
            />
          </div>
          {/*<div id="opiniones">
            <ClientesSection />
          </div>*/}
          {/*<div id="galeria">
            <GallerySection />
          </div>*/}
          {/*<div id="contacto">
            <ContactoSection />
          </div>*/}
          {/*<div id="footer">
            <Footer />
          </div>*/}
        </div>
      </div>
    </CartProvider>
    </CustomerIdentityProvider>
  );
}

export default App;